import React, { useState } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import cx from 'classnames';
import { postContactInfo } from 'govbr.frontend.institucional/actions';
import { Message } from 'govbr.frontend.policy/components';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import styles from './Form.module.less';

const hcaptchaRef = React.createRef();

const messages = defineMessages({
  name: {
    id: 'Name',
    defaultMessage: 'Name',
  },
  nameErrorMessage: {
    id: 'Name must be provided',
    defaultMessage: 'Name must be provided',
  },
  EMail: {
    id: 'E-mail',
    defaultMessage: 'E-mail',
  },
  emailErrorMessage: {
    id: 'Enter a valid e-mail address',
    defaultMessage: 'Enter a valid e-mail address',
  },
  phoneNumber: {
    id: 'Phone number',
    defaultMessage: 'Phone number',
  },
  phoneNumberErrorMessage: {
    id: 'Enter a valid phone number',
    defaultMessage: 'Enter a valid phone number',
  },
  subject: {
    id: 'Subject',
    defaultMessage: 'Subject',
  },
  subjectErrorMessage: {
    id: 'Subject must be provided',
    defaultMessage: 'Subject must be provided',
  },
  hcaptchaErrorMessage: {
    id: 'Complete reCAPTCHA',
    defaultMessage: 'Complete reCAPTCHA',
  },
  message: {
    id: 'Message',
    defaultMessage: 'Message',
  },
  messageErrorMessage: {
    id: 'Message must be provided',
    defaultMessage: 'Message must be provided',
  },
  send: {
    id: 'Send',
    defaultMessage: 'Send',
  },
  sending: {
    id: 'Sending',
    defaultMessage: 'Sending',
  },
  optional: {
    id: 'optional',
    defaultMessage: 'optional',
  },
});

const ContactInput = ({
  id,
  label,
  value,
  onChange,
  error,
  type = 'text',
  addon,
}) => {
  const classes = cx('br-input', { danger: !!error });

  return (
    <div className={classes}>
      <label htmlFor={id}>
        {label}
        {!!addon && <span>({addon})</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? (
        <span className="feedback danger" role="alert">
          <span className="fas fa-times-circle" aria-hidden="true"></span>
          {error}
        </span>
      ) : null}
    </div>
  );
};

const ContactTextArea = ({ id, label, value, onChange, error }) => {
  const classes = cx('br-textarea', { danger: !!error });

  return (
    <div className={classes}>
      <label htmlFor={id}>{label}</label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {error ? (
        <span className="feedback danger" role="alert">
          <span className="fas fa-times-circle" aria-hidden="true"></span>
          {error}
        </span>
      ) : null}
    </div>
  );
};

const ContactHcaptcha = ({ id, error, sitekey, onVerify }) => {
  const classes = cx('br-input', { danger: !!error });

  return (
    <div className={classes}>
      <HCaptcha sitekey={sitekey} onVerify={onVerify} ref={hcaptchaRef} />
      {error ? (
        <span className="feedback danger" role="alert">
          <span className="fas fa-times-circle" aria-hidden="true"></span>
          {error}
        </span>
      ) : null}
    </div>
  );
};

const emailTemplate = (name, email, phoneNumber, message, siteName) =>
  `Prezado Gestor,

A seguinte mensagem foi enviada por ${name} (${email}${
    phoneNumber ? ', ' : ''
  }${phoneNumber}) através do Fale Conosco:

${message}

Atenciosamente,

${siteName}`;

const Form = ({ to, 'site-name': siteName }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [hcaptcha, setHcaptcha] = useState('');

  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [hcaptchaError, setHcaptchaError] = useState('');

  const handleHcaptchaChange = (value) => {
    setHcaptcha(value);
  };

  const intl = useIntl();
  const dispatch = useDispatch();

  const useEmailSend = () => {
    const loading = useSelector(
      (state) => state.contact_info_email_send.loading,
    );
    const loaded = useSelector((state) => state.contact_info_email_send.loaded);
    const error = useSelector((state) => state.contact_info_email_send.error);
    return { loading, loaded, error };
  };
  const { loaded, loading, error } = useEmailSend();

  const contactInfoState = useSelector((state) => state.contact_info);
  const public_key = contactInfoState.data.hcaptcha_public_key;

  const clearErrors = () => {
    setNameError('');
    setEmailError('');
    setPhoneNumberError('');
    setSubjectError('');
    setMessageError('');
    setHcaptchaError('');
  };

  const validateForm = () => {
    clearErrors();
    let hasErrors = false;

    if (!name || name.trim() === '') {
      setNameError(intl.formatMessage(messages.nameErrorMessage));
      hasErrors = true;
    }

    // const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

    if (!email || email.trim() === '' || !email.match(emailRegex)) {
      setEmailError(intl.formatMessage(messages.emailErrorMessage));
      hasErrors = true;
    }

    if (!subject || subject.trim() === '') {
      setSubjectError(intl.formatMessage(messages.subjectErrorMessage));
      hasErrors = true;
    }

    if (!message || message.trim() === '') {
      setMessageError(intl.formatMessage(messages.messageErrorMessage));
      hasErrors = true;
    }

    if (!hcaptcha || hcaptcha.trim() === '') {
      setHcaptchaError(intl.formatMessage(messages.hcaptchaErrorMessage));
      hasErrors = true;
    }

    return !hasErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const body = emailTemplate(name, email, phoneNumber, message, siteName);

    dispatch(postContactInfo(name, email, to, subject, body, hcaptcha));
  };

  return (
    <div className={styles.form}>
      {error ? (
        <Message type="danger" fixed inline={true} title="Aconteceu um erro">
          {error.message}
        </Message>
      ) : null}

      {loaded ? (
        <Message type="success" fixed inline title="Enviada com sucesso">
          {/* {error.message} */}
        </Message>
      ) : null}

      <form className="container-fluid" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-12">
            <ContactInput
              id="contact-name"
              label={intl.formatMessage(messages.name)}
              value={name}
              error={nameError}
              onChange={(v) => setName(v)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-7">
            <ContactInput
              id="contact-email"
              label={intl.formatMessage(messages.EMail)}
              value={email}
              error={emailError}
              onChange={(v) => setEmail(v)}
            />
          </div>
          <div className="col-5">
            <ContactInput
              id="contact-phone-number"
              label={intl.formatMessage(messages.phoneNumber)}
              addon={intl.formatMessage(messages.optional)}
              value={phoneNumber}
              error={phoneNumberError}
              optional
              onChange={(v) => setPhoneNumber(v)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <ContactInput
              id="contact-subject"
              label={intl.formatMessage(messages.subject)}
              value={subject}
              error={subjectError}
              onChange={(v) => setSubject(v)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <ContactTextArea
              id="contact-body"
              label={intl.formatMessage(messages.message)}
              value={message}
              error={messageError}
              onChange={(v) => setMessage(v)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col">
            <ContactHcaptcha
              id="contact-hcaptcha"
              error={hcaptchaError}
              sitekey={public_key}
              onVerify={handleHcaptchaChange}
            />
          </div>
        </div>
        <div className="row justify-content-center">
          {loading ? (
            <button className="br-button primary loading" type="button">
              {intl.formatMessage(messages.sending)}
            </button>
          ) : (
            <button className="br-button primary" type="submit">
              {intl.formatMessage(messages.send)}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default Form;
